import React, { useEffect, useMemo, useState } from 'react';
import PickUpDisplaySettingForm from '@components/common/form/PickUpDisplaySettingModal/PickUpDisplaySettingForm';
import { MediaKinds } from '@constants';
import apiConfig from '@constants/apiConfig';
import useFetch from '@hooks/useFetch';
import useTranslate from '@hooks/useTranslate';
import { commonMessage } from '@locales/intl';
import { showErrorMessage, showSucsessMessage } from '@services/notifyService';
import { Checkbox, Flex, Modal } from 'antd';

const parseSettings = (raw) => {
    let parsed = {};
    try {
        parsed = raw ? JSON.parse(raw) : {};
    } catch (e) {
        parsed = {};
    }
    const tabletRandom = parsed.tablet_random ?? {};
    const timeWork = parsed.time_work ?? {};
    const medias = parsed.medias ?? tabletRandom.medias ?? [];
    return {
        time_work: timeWork,
        selected_media: parsed.selected_media ?? null,
        tablet_random: {
            ...tabletRandom,
            medias,
            media_kind: tabletRandom.media_kind || MediaKinds.IMAGE,
        },
    };
};

const PickUpDisplaySettingModal = ({ open, onClose, selectedRecord, restaurantId, onSaveSuccess }) => {
    const translate = useTranslate();
    const [isChanged, setIsChanged] = useState(false);
    const [generalTimeWork, setGeneralTimeWork] = useState(null);
    const [applyForAll, setApplyForAll] = useState(false);

    const settingObj = useMemo(
        () => parseSettings(selectedRecord?.device?.settings),
        [selectedRecord],
    );

    const { execute: uploadFile } = useFetch({
        ...apiConfig.file.upload,
        isRequiredTenantId: true,
    });

    const { execute: update, loading: loadingUpdate } = useFetch(apiConfig.orderDisplay.updateSettings);
    const { execute: executeGet } = useFetch(apiConfig.restaurant.getById, { immediate: false });

    useEffect(() => {
        if (open) {
            setIsChanged(false);
            setApplyForAll(false);
        }
    }, [open, selectedRecord]);

    useEffect(() => {
        if (open && restaurantId) {
            executeGet({
                pathParams: { id: restaurantId },
                onCompleted: (response) => {
                    const settings = JSON.parse(response.data.settings || '{}');
                    setGeneralTimeWork(settings.general_time_work);
                },
                onError: () => {},
            });
        }
    }, [open, restaurantId]);

    const handleSave = ({ settings }) => {
        if (!selectedRecord) return;
        const medias = settings?.tablet_random?.medias || [];
        const time_work = settings?.time_work;
        const selected_media = settings?.selected_media ?? null;

        update({
            data: {
                id: selectedRecord.id,
                settings: JSON.stringify({ medias, time_work, selected_media }),
                applyForAll,
            },
            onCompleted: () => {
                showSucsessMessage(translate.formatMessage(commonMessage.success), translate);
                onSaveSuccess();
                onClose();
            },
            onError: () => {
                showErrorMessage(translate.formatMessage(commonMessage.error), translate);
            },
        });
    };

    return (
        <Modal
            open={open}
            onCancel={onClose}
            footer={null}
            title={
                <Flex align="center" justify="space-between" gap={16} style={{ width: '100%', paddingRight: 24 }}>
                    <span>{translate.formatMessage(commonMessage.pickUpDisplay)}</span>
                    <Checkbox
                        checked={applyForAll}
                        onChange={(e) => {
                            setApplyForAll(e.target.checked);
                            setIsChanged(true);
                        }}
                        style={{ fontWeight: 'normal' }}
                    >
                        {translate.formatMessage(commonMessage.applyForAllDevice)}
                    </Checkbox>
                </Flex>
            }
            width={1100}
            destroyOnClose
            maskClosable={false}
            style={{ top: 50 }}
            styles={{
                body: {
                    maxHeight: '80vh',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    paddingRight: '2px',
                },
            }}
        >
            {selectedRecord && (
                <PickUpDisplaySettingForm
                    formId="pick-up-display-form"
                    settingObj={settingObj}
                    onSubmit={handleSave}
                    uploadFile={uploadFile}
                    setIsChangedFormValues={setIsChanged}
                    isChangedFormValues={isChanged}
                    loadingUpdate={loadingUpdate}
                    generalTimeWork={generalTimeWork}
                />
            )}
        </Modal>
    );
};

export default PickUpDisplaySettingModal;